import { useTranslate } from '@refinedev/core';
import { Card, Descriptions, Flex, Table, Tag, Typography, theme } from 'antd';
import type { DescriptionsProps } from 'antd';
import type { CategoryDetailOut, SectionOut } from '@/api/generated/schemas';
import { DisplayMode } from '@/api/generated/schemas';
import { LabelsTags } from '@/components/common/LabelsField';
import { ShowIconLink } from '@/components/common/ShowIconLink';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { formatDateTime } from '@/utils/format';

const { Text } = Typography;

/** Union `collections` rows come from different source tables — link each
 * section to the module that actually owns the record. */
const COLLECTION_KIND_RESOURCE: Record<string, string> = {
  category: 'user_catalog.categories',
  subcategory: 'user_catalog.managed_collections',
  editorial: 'user_catalog.managed_collections',
  personal: 'user_catalog.managed_collections',
  author: 'user_catalog.authors',
  lector: 'user_catalog.lectors',
  publisher: 'user_catalog.publishers',
  series: 'user_catalog.series',
  tag: 'user_catalog.tags',
  emotion: 'user_catalog.emotions',
  banner: 'user_catalog.banners',
};

interface CategoryGeneralTabProps {
  record: CategoryDetailOut;
}

export const CategoryGeneralTab = ({ record }: CategoryGeneralTabProps) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();

  const sections = record.sections ?? [];

  const items: DescriptionsProps['items'] = [
    {
      key: 'id',
      label: t('base.fields.id'),
      children: orDash(record.id),
    },
    {
      key: 'name',
      label: t('user_catalog.categories.fields.name'),
      children: orDash(record.name),
    },
    {
      key: 'slug',
      label: t('user_catalog.categories.fields.slug'),
      children: orDash(record.slug && <Text code>{record.slug}</Text>),
    },
    {
      key: 'position',
      label: t('user_catalog.categories.fields.position'),
      children: orDash(record.position),
    },
    {
      key: 'labels',
      label: t('base.labels._field'),
      children: <LabelsTags labels={record.labels} />,
    },
    {
      key: 'description',
      label: t('user_catalog.categories.fields.description'),
      children: orDash(record.description),
    },
    {
      key: 'created_at',
      label: t('base.fields.created_at'),
      children: orDash(formatDateTime(record.created_at)),
    },
    {
      key: 'updated_at',
      label: t('base.fields.updated_at'),
      children: orDash(formatDateTime(record.updated_at)),
    },
  ];

  return (
    <Flex vertical gap={token.margin}>
      <Card title={t('base.sections.basic_info')}>
        <Descriptions
          {...verticalDescProps}
          column={{ xs: 1, sm: 2, md: 3 }}
          items={items}
        />
      </Card>

      {sections.length > 0 && (
        <Card
          title={`${t('user_catalog.categories.sections.title')} (${sections.length})`}
        >
          <Table<SectionOut>
            dataSource={sections}
            rowKey="id"
            pagination={false}
            size="small"
          >
            <Table.Column dataIndex="id" title={t('base.fields.id')} />
            <Table.Column
              dataIndex="position"
              title={t('user_catalog.categories.sections.fields.position')}
            />
            <Table.Column
              title={t('user_catalog.common.display_mode._field')}
              render={(_: unknown, section: SectionOut) => (
                // Regular is the default and the majority — leave it quiet so
                // the rows that change the layout stand out.
                <Tag
                  color={
                    section.display_mode === DisplayMode.regular
                      ? undefined
                      : 'blue'
                  }
                >
                  {t(
                    `user_catalog.common.display_mode.${section.display_mode}`
                  )}
                </Tag>
              )}
            />
            <Table.Column
              title={t('user_catalog.categories.fields.collection_kind')}
              render={(_: unknown, section: SectionOut) =>
                orDash(
                  section.collection?.collection_kind &&
                    t(
                      `user_catalog.categories.collection_kind.${section.collection.collection_kind}`
                    )
                )
              }
            />
            <Table.Column
              title={t('user_catalog.categories.sections.fields.collection')}
              render={(_: unknown, section: SectionOut) => (
                <ShowIconLink
                  resource={
                    COLLECTION_KIND_RESOURCE[
                      section.collection?.collection_kind ?? ''
                    ] ?? 'user_catalog.collections'
                  }
                  id={section.collection_id}
                >
                  {section.collection?.name ?? section.collection_id}
                </ShowIconLink>
              )}
            />
          </Table>
        </Card>
      )}
    </Flex>
  );
};
